import { Component, OnInit } from '@angular/core';
import { AvanoMenuEntry } from '@avano/avano-components';
import { Ability } from '@casl/ability';

import { AbilityActions, AbilitySites, EnvironmentConfigurationService, ScipUiSettings, ScipUiSettingsService } from './scip/services';
import { routes } from './app.routes';
import { scipPreferencesRoutes } from './scip/pages/scip-preferences/scip-preferences.routes';
import { translationKeys } from './scip/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'scip-menu',
  templateUrl: './scip.menu.component.html',
  styleUrls: ['./scip.menu.component.scss']
})
export class ScipMenuComponent implements OnInit {
  appVersion: string;

  menuEntries: AvanoMenuEntry[] = [];

  initialCollapseState: Observable<boolean>;

  private dashboardTranslationKey = translationKeys.appmenucomponent.typescript.dashboardLabel;

  private productTranslationKey = translationKeys.appmenucomponent.typescript.productLabel;
  private submissionTranslationKey = translationKeys.appmenucomponent.typescript.submissionLabel;

  private scipPreferencesTranslationKey = translationKeys.appmenucomponent.typescript.scipPreferences;
  private echaProfileSetupTranslationKey = translationKeys.appmenucomponent.typescript.echaProfileSetup;
  private dossierDefaultsTranslationKey = translationKeys.appmenucomponent.typescript.dossierDefaults;

  constructor(private env: EnvironmentConfigurationService, private ability: Ability, private scipUiSettings: ScipUiSettingsService) {}

  ngOnInit(): void {
    // Lets read our current scip version to show it in the avano-menu-component at the bottom
    this.appVersion = this.env.environment.scipVersion;

    // We can subscribe without any "unsubscribe"/takeUntil, because the menu lives as long as the application itself
    this.initialCollapseState = this.scipUiSettings.getScipUiSettings().pipe(map((allSettings: ScipUiSettings) => allSettings.menu.collapsed));

    this.setupMenuEntries();
  }

  changedCollapse(isCollapsed: boolean): void {
    // The menu is emitting one time with "null", but we want to ignore that
    if (isCollapsed !== null) {
      this.scipUiSettings.saveMenuSettings({ collapsed: isCollapsed });
    }
  }

  private setupMenuEntries(): void {
    // If the user does not have any right he will not get at this stage, so we can safely assume that
    // he has at least one scip-permission, so we will add the dashboard here if its not disabled
    if (!this.env.environment.dashboardHidden) {
      const dashboardEntry: AvanoMenuEntry = {
        label: this.dashboardTranslationKey,
        icon: 'icon-mainmenu-dashboard',
        routerLink: `/${routes.dashboard}`
      };
      this.menuEntries.push(dashboardEntry);
    }

    if (this.ability.can(AbilityActions.read, AbilitySites.product)) {
      const productsEntry: AvanoMenuEntry = {
        label: this.productTranslationKey,
        icon: 'icon-mainmenu-parts',
        routerLink: `/${routes.products}`
      };
      this.menuEntries.push(productsEntry);
    }

    if (this.ability.can(AbilityActions.read, AbilitySites.submission)) {
      const submissionsEntry: AvanoMenuEntry = {
        label: this.submissionTranslationKey,
        icon: 'icon-mainmenu-parent-record-card',
        routerLink: `/${routes.submissions}`
      };
      this.menuEntries.push(submissionsEntry);
    }

    if (this.ability.can(AbilityActions.read, AbilitySites.preferences)) {
      const preferencesEntry: AvanoMenuEntry = {
        label: this.scipPreferencesTranslationKey,
        icon: 'icon-mainmenu-settings',
        children: [
          {
            label: this.echaProfileSetupTranslationKey,
            routerLink: `/${routes.scipPreferences}/${scipPreferencesRoutes.echaProfileSetup}`
          },
          {
            label: this.dossierDefaultsTranslationKey,
            routerLink: `/${routes.scipPreferences}/${scipPreferencesRoutes.dossierDefaults}`
          }
        ]
      };
      this.menuEntries.push(preferencesEntry);
    }
  }
}
